import { downloadAxiosPdfResponse } from '../utils/pdfDownload';
import http from './http';

const api = http;

// Customer API functions
export const customerApi = {
  // Get all customers
  getCustomers: async (params = {}) => {
    const response = await api.get('/api/customers', { params });
    return response.data;
  },

  // Get customer by ID
  getCustomer: async (id) => {
    const response = await api.get(`/api/customers/${id}`);
    return response.data;
  },

  // Create new customer
  createCustomer: async (customerData) => {
    const response = await api.post('/api/customers', customerData);
    return response.data;
  },

  // Update customer
  updateCustomer: async (id, customerData) => {
    const response = await api.put(`/api/customers/${id}`, customerData);
    return response.data;
  },

  // Delete customer
  deleteCustomer: async (id) => {
    const response = await api.delete(`/api/customers/${id}`);
    return response.data;
  },

  // Sync customers from Google Sheets
  syncCustomersFromGoogleSheets: async (sheetUrl) => {
    const response = await api.post('/api/customers/sync-google-sheets', { sheetUrl });
    return response.data;
  },
};

// Estimate API functions
export const estimateApi = {
  // Get all estimates
  getEstimates: async () => {
    const response = await api.get('/api/estimates');
    return response.data;
  },

  // Get estimates for current user (customer portal)
  getMyEstimates: async () => {
    const response = await api.get('/api/estimates/mine');
    return response.data;
  },

  // Get estimate by ID (includes relatedContractsSummary)
  getEstimate: async (id) => {
    const response = await api.get(`/api/estimates/${id}`);
    return response.data;
  },

  // Get contracts created from an estimate
  getContractsForEstimate: async (estimateId) => {
    const response = await api.get('/api/contracts', { params: { estimateId } });
    return response.data;
  },

  // Create new estimate
  createEstimate: async (estimateData) => {
    const response = await api.post('/api/estimates', estimateData);
    return response.data;
  },

  // Update estimate
  updateEstimate: async (id, estimateData) => {
    const response = await api.put(`/api/estimates/${id}`, estimateData);
    return response.data;
  },

  updateStatus: async (id, status, extras = {}) => {
    const response = await api.patch(`/api/estimates/${id}/status`, { status, ...extras });
    return response.data;
  },

  // Delete estimate
  deleteEstimate: async (id) => {
    const response = await api.delete(`/api/estimates/${id}`);
    return response.data;
  },

  // Download estimate PDF (server endpoint expected at /api/estimates/:id/pdf)
  downloadPdf: async (id) => {
    const response = await api.get(`/api/estimates/${id}/pdf`, { responseType: 'blob' });
    downloadAxiosPdfResponse(response, 'Estimate.pdf');
    return { success: true };
  },
};

// Contract API functions
export const contractApi = {
  // Get all contracts (optional estimateId filter)
  getContracts: async (params = {}) => {
    const response = await api.get('/api/contracts', { params });
    return response.data;
  },

  // Get contracts for current user (customer portal)
  getMyContracts: async () => {
    const response = await api.get('/api/contracts/mine');
    return response.data;
  },

  // Get contract by ID
  getContract: async (id) => {
    const response = await api.get(`/api/contracts/${id}`);
    return response.data;
  },

  // Create new contract
  createContract: async (contractData) => {
    const response = await api.post('/api/contracts', contractData);
    return response.data;
  },

  // Update contract
  updateContract: async (id, contractData) => {
    const response = await api.put(`/api/contracts/${id}`, contractData);
    return response.data;
  },

  updateStatus: async (id, status, extras = {}) => {
    const response = await api.patch(`/api/contracts/${id}/status`, { status, ...extras });
    return response.data;
  },

  // Delete contract
  deleteContract: async (id) => {
    const response = await api.delete(`/api/contracts/${id}`);
    return response.data;
  },

  // Download contract PDF
  downloadPdf: async (id) => {
    const response = await api.get(`/api/contracts/${id}/pdf`, {
      responseType: 'blob'
    });
    downloadAxiosPdfResponse(response, 'Painting_Contract.pdf');
    return { success: true };
  },

  // BoldSign Integration
  sendForSignature: async (contractId, contractorInfo = {}) => {
    const response = await api.post(`/api/contracts/${contractId}/send-for-signature`, contractorInfo);
    return response.data;
  },

  getSignatureStatus: async (contractId) => {
    const response = await api.get(`/api/contracts/${contractId}/signature-status`);
    return response.data;
  },

  downloadSignedDocument: async (contractId) => {
    const response = await api.get(`/api/contracts/${contractId}/signed-document`, {
      responseType: 'blob'
    });
    return response.data;
  },

  resendSignature: async (contractId, signerEmail) => {
    const response = await api.post(`/api/contracts/${contractId}/resend-signature`, { signerEmail });
    return response.data;
  },

  cancelSignature: async (contractId) => {
    const response = await api.post(`/api/contracts/${contractId}/cancel-signature`);
    return response.data;
  },
};

// Amendment API functions
export const amendmentApi = {
  // Get all amendments
  getAmendments: async () => {
    const response = await api.get('/api/amendments');
    return response.data;
  },

  // Get amendments for a specific contract
  getContractAmendments: async (contractId) => {
    const response = await api.get(`/api/amendments/contract/${contractId}`);
    return response.data;
  },

  // Get contract summary with amendments
  getContractSummary: async (contractId) => {
    const response = await api.get(`/api/amendments/contract/${contractId}/summary`);
    return response.data;
  },

  // Get amendment by ID
  getAmendment: async (id) => {
    const response = await api.get(`/api/amendments/${id}`);
    return response.data;
  },

  // Create new amendment
  createAmendment: async (amendmentData) => {
    const response = await api.post('/api/amendments', amendmentData);
    return response.data;
  },

  // Update amendment
  updateAmendment: async (id, amendmentData) => {
    const response = await api.put(`/api/amendments/${id}`, amendmentData);
    return response.data;
  },

  // Delete amendment
  deleteAmendment: async (id) => {
    const response = await api.delete(`/api/amendments/${id}`);
    return response.data;
  },

  // Approve amendment
  approveAmendment: async (id, approvedBy) => {
    const response = await api.post(`/api/amendments/${id}/approve`, { approvedBy });
    return response.data;
  },

  // Reject amendment
  rejectAmendment: async (id, rejectedBy, rejectionReason) => {
    const response = await api.post(`/api/amendments/${id}/reject`, { rejectedBy, rejectionReason });
    return response.data;
  },

  // Download amendment PDF
  downloadPdf: async (id) => {
    const response = await api.get(`/api/amendments/${id}/pdf`, {
      responseType: 'blob'
    });
    downloadAxiosPdfResponse(response, 'Contract_Amendment.pdf');
    return { success: true };
  },
};

// Job API functions
export const jobApi = {
  // Get all jobs
  getJobs: async () => {
    const response = await api.get('/api/jobs');
    return response.data;
  },

  // Get job by ID
  getJob: async (id) => {
    const response = await api.get(`/api/jobs/${id}`);
    return response.data;
  },

  // Create new job
  createJob: async (jobData) => {
    const response = await api.post('/api/jobs', jobData);
    return response.data;
  },

  // Update job
  updateJob: async (id, jobData) => {
    const response = await api.put(`/api/jobs/${id}`, jobData);
    return response.data;
  },

  // Delete job
  deleteJob: async (id) => {
    const response = await api.delete(`/api/jobs/${id}`);
    return response.data;
  },
};

// Client Report API functions
export const clientReportApi = {
  // Get all reports
  getReports: async (params = {}) => {
    const response = await api.get('/api/client-reports', { params });
    return response.data;
  },

  // Get report by ID
  getReport: async (id) => {
    const response = await api.get(`/api/client-reports/${id}`);
    return response.data;
  },

  // Final line items for a contract (original + approved amendments)
  getContractLineItems: async (contractId) => {
    const response = await api.get(`/api/client-reports/contract-line-items/${contractId}`);
    return response.data;
  },

  // Create new report
  createReport: async (reportData) => {
    const response = await api.post('/api/client-reports', reportData);
    return response.data;
  },

  // Create report from job
  createReportFromJob: async (jobId, additionalData = {}) => {
    const response = await api.post(`/api/client-reports/from-job/${jobId}`, additionalData);
    return response.data;
  },

  // Update report
  updateReport: async (id, reportData) => {
    const response = await api.put(`/api/client-reports/${id}`, reportData);
    return response.data;
  },

  // Delete report
  deleteReport: async (id) => {
    const response = await api.delete(`/api/client-reports/${id}`);
    return response.data;
  },

  // Finalize/approve a report
  finalizeReport: async (id) => {
    const response = await api.post(`/api/client-reports/${id}/finalize`);
    return response.data;
  },

  // Upload images to report
  uploadImages: async (id, files, captions = [], categories = [], taskNumbers = [], lineItemNumber = null) => {
    const formData = new FormData();
    
    files.forEach(file => {
      formData.append('images', file);
    });
    
    captions.forEach(caption => {
      formData.append('captions[]', caption);
    });
    
    categories.forEach(category => {
      formData.append('categories', category);
    });
    
    taskNumbers.forEach(taskNumber => {
      formData.append('taskNumbers[]', taskNumber);
    });

    if (lineItemNumber != null && lineItemNumber !== '') {
      formData.append('lineItemNumber', lineItemNumber);
    }

    const response = await api.post(`/api/client-reports/${id}/images`, formData);
    return response.data;
  },

  // Download report PDF
  downloadPdf: async (id, filename = 'Inspection_Report.pdf') => {
    const response = await api.get(`/api/client-reports/${id}/pdf`, {
      responseType: 'blob'
    });
    downloadAxiosPdfResponse(response, filename);
    return { success: true };
  },

  // Send report to client
  sendToClient: async (id) => {
    const response = await api.post(`/api/client-reports/${id}/send`);
    return response.data;
  },

  // Update task status
  updateTaskStatus: async (id, taskNumber, status) => {
    const response = await api.put(`/api/client-reports/${id}/tasks/${taskNumber}`, { status });
    return response.data;
  },
};

// Invoice API functions
export const invoiceApi = {
  // Get all invoices
  getInvoices: async (params = {}) => {
    const response = await api.get('/api/invoices', { params });
    return response.data;
  },

  // Get invoice by ID
  getInvoice: async (id) => {
    const response = await api.get(`/api/invoices/${id}`);
    return response.data;
  },

  // Create new invoice
  createInvoice: async (invoiceData) => {
    const response = await api.post('/api/invoices', invoiceData);
    return response.data;
  },

  // Update invoice
  updateInvoice: async (id, invoiceData) => {
    const response = await api.put(`/api/invoices/${id}`, invoiceData);
    return response.data;
  },

  // Delete invoice
  deleteInvoice: async (id) => {
    const response = await api.delete(`/api/invoices/${id}`);
    return response.data;
  },

  // Download invoice PDF
  downloadPdf: async (id) => {
    const response = await api.get(`/api/invoices/${id}/pdf`, {
      responseType: 'blob'
    });
    downloadAxiosPdfResponse(response, 'Invoice.pdf');
    return { success: true };
  },
};

// Receipt API functions
export const receiptApi = {
  // Get all receipts
  getReceipts: async (params = {}) => {
    const response = await api.get('/api/invoices/receipts', { params });
    return response.data;
  },

  // Get receipt by ID
  getReceipt: async (id) => {
    const response = await api.get(`/api/invoices/receipts/${id}`);
    return response.data;
  },

  // Create new receipt
  createReceipt: async (receiptData) => {
    const response = await api.post('/api/invoices/receipts', receiptData);
    return response.data;
  },

  // Download receipt PDF
  downloadPdf: async (id) => {
    const response = await api.get(`/api/invoices/receipts/${id}/pdf`, {
      responseType: 'blob'
    });
    downloadAxiosPdfResponse(response, 'Receipt.pdf');
    return { success: true };
  },
};

// Payment Request API functions
export const paymentRequestApi = {
  // Get all payment requests
  getPaymentRequests: async (params = {}) => {
    const response = await api.get('/api/invoices/payment-requests', { params });
    return response.data;
  },

  // Get payment request by ID
  getPaymentRequest: async (id) => {
    const response = await api.get(`/api/invoices/payment-requests/${id}`);
    return response.data;
  },

  // Create new payment request
  createPaymentRequest: async (requestData) => {
    const response = await api.post('/api/invoices/payment-requests', requestData);
    return response.data;
  },

  // Update payment request
  updatePaymentRequest: async (id, requestData) => {
    const response = await api.put(`/api/invoices/payment-requests/${id}`, requestData);
    return response.data;
  },
};

export default api;
